'use client';

import { Skeleton } from '@mui/material';
import { Mark, mergeAttributes } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowUp,
  FaBold,
  FaCode,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaStrikethrough,
  FaUnderline,
} from 'react-icons/fa6';
import { LuCodeXml } from 'react-icons/lu';

import { CloseIcon } from '../../atomic-atoms';
import { MainButton } from '../../atomic-atoms/buttons/MainButton';
import { SecondaryButton } from '../../atomic-atoms/buttons/SecondaryButton';
import { Tooltip } from '../../atomic-atoms/Tooltip';
import { Modal } from '../Modal';
import { SearchBar } from '../SearchBar';
import { SmartInput } from '../SmartInput/SmartInput';

import styles from './RichTextEditor.module.scss';

import type { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import type { ReactNode } from 'react';

export const DEFAULT_FORMATTING_OPTIONS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'link',
  'orderedList',
  'bulletList',
  'code',
  'codeBlock',
] as const;

export type RichTextEditorFormattingOption =
  (typeof DEFAULT_FORMATTING_OPTIONS)[number];

export type RichTextEditorValue = {
  html: string;
  canonicalHtml: string;
  text: string;
  isEmpty: boolean;
};

export type RichTextEditorSuggestionItem = {
  id: string;
  label: string;
  description?: string;
  canonicalValue: string;
};

export type RichTextEditorSuggestionConfig = {
  trigger: '@' | '[[';
  label: string;
  getItems: (
    query: string,
  ) => Promise<RichTextEditorSuggestionItem[]> | RichTextEditorSuggestionItem[];
};

type RichTextEditorProps = {
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  actionSlot?: ReactNode;
  showSubmitButton?: boolean;
  submitAriaLabel?: string;
  isSubmitDisabled?: boolean;
  showToolbar?: boolean;
  formattingOptions?: RichTextEditorFormattingOption[];
  extraExtensions?: Extension[];
  suggestions?: RichTextEditorSuggestionConfig[];
  onSubmit?: (payload: RichTextEditorValue) => void;
  onCancel?: () => void;
  onChange?: (payload: RichTextEditorValue) => void;
};

type ActiveSuggestion = {
  config: RichTextEditorSuggestionConfig;
  query: string;
  range: {
    from: number;
    to: number;
  };
};

const RichTextToken = Mark.create({
  name: 'richTextToken',

  addAttributes() {
    return {
      canonicalValue: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-rich-text-token'),
        renderHTML: (attributes) =>
          attributes.canonicalValue
            ? { 'data-rich-text-token': attributes.canonicalValue }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-rich-text-token]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'rich-text-token' }),
      0,
    ];
  },
});

type ToolbarItem = {
  key: RichTextEditorFormattingOption;
  label: string;
  icon: ReactNode;
  isActive: (editor: Editor) => boolean;
  run: (editor: Editor, actions: ToolbarActions) => void;
};

type ToolbarActions = {
  openLinkDialog: (currentUrl: string) => void;
};

function isSafeLinkUrl(url: string) {
  return /^(https?:|mailto:)/i.test(url);
}

function getAnchorFromTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest('a');
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function toCanonicalHtml(html: string) {
  return html.replace(
    /<span\b[^>]*data-rich-text-token="([^"]+)"[^>]*>.*?<\/span>/g,
    (_match, canonicalValue: string) => canonicalValue,
  );
}

function getEditorValue(editor: Editor): RichTextEditorValue {
  const html = editor.getHTML();

  return {
    html,
    canonicalHtml: toCanonicalHtml(html),
    text: editor.getText({ blockSeparator: '\n' }),
    isEmpty: editor.isEmpty,
  };
}

function getSuggestionMatch(
  editor: Editor,
  suggestions: RichTextEditorSuggestionConfig[],
): ActiveSuggestion | null {
  if (!suggestions.length || !editor.state.selection.empty) {
    return null;
  }

  const { $from } = editor.state.selection;
  const textBefore = $from.parent.textBetween(0, $from.parentOffset);

  const matches = suggestions
    .map((config) => {
      const triggerIndex = textBefore.lastIndexOf(config.trigger);

      if (triggerIndex < 0) {
        return null;
      }

      const prefix = textBefore[triggerIndex - 1];
      if (config.trigger === '@' && prefix && !/\s/.test(prefix)) {
        return null;
      }

      const query = textBefore.slice(triggerIndex + config.trigger.length);
      if (query.includes('\n') || query.includes(']')) {
        return null;
      }

      if (config.trigger === '@' && /\s/.test(query)) {
        return null;
      }

      return {
        config,
        query,
        triggerIndex,
        range: {
          from: editor.state.selection.from - textBefore.length + triggerIndex,
          to: editor.state.selection.from,
        },
      };
    })
    .filter(Boolean)
    .sort((a, b) => b!.triggerIndex - a!.triggerIndex);

  const match = matches[0];
  if (!match) {
    return null;
  }

  return {
    config: match.config,
    query: match.query,
    range: match.range,
  };
}

function makeToolbarItems(): ToolbarItem[] {
  const iconSize = 16;
  return [
    {
      key: 'bold',
      label: 'Bold • Ctrl/Cmd+B',
      icon: <FaBold size={iconSize} />,
      isActive: (editor) => editor.isActive('bold'),
      run: (editor) => editor.chain().focus().toggleBold().run(),
    },
    {
      key: 'italic',
      label: 'Italic • Ctrl/Cmd+I',
      icon: <FaItalic size={iconSize} />,
      isActive: (editor) => editor.isActive('italic'),
      run: (editor) => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: 'underline',
      label: 'Underline • Ctrl/Cmd+U',
      icon: <FaUnderline size={iconSize} />,
      isActive: (editor) => editor.isActive('underline'),
      run: (editor) => editor.chain().focus().toggleUnderline().run(),
    },
    {
      key: 'strike',
      label: 'Strike • Ctrl/Cmd+Shift+X',
      icon: <FaStrikethrough size={iconSize} />,
      isActive: (editor) => editor.isActive('strike'),
      run: (editor) => editor.chain().focus().toggleStrike().run(),
    },
    {
      key: 'link',
      label: 'Link • Ctrl/Cmd+L',
      icon: <FaLink size={iconSize} />,
      isActive: (editor) => editor.isActive('link'),
      run: (editor, actions) => {
        const currentUrl = editor.getAttributes('link').href ?? '';
        actions.openLinkDialog(currentUrl);
      },
    },
    {
      key: 'orderedList',
      label: 'Numbered list • Ctrl/Cmd+Shift+7',
      icon: <FaListOl size={iconSize} />,
      isActive: (editor) => editor.isActive('orderedList'),
      run: (editor) => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: 'bulletList',
      label: 'Bulleted list • Ctrl/Cmd+Shift+8',
      icon: <FaListUl size={iconSize} />,
      isActive: (editor) => editor.isActive('bulletList'),
      run: (editor) => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: 'code',
      label: 'Inline code • Ctrl/Cmd+E',
      icon: <FaCode size={iconSize} />,
      isActive: (editor) => editor.isActive('code'),
      run: (editor) => editor.chain().focus().toggleCode().run(),
    },
    {
      key: 'codeBlock',
      label: 'Code block • Ctrl/Cmd+Alt+C',
      icon: <LuCodeXml size={iconSize} />,
      isActive: (editor) => editor.isActive('codeBlock'),
      run: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    },
  ];
}

function Toolbar({
  editor,
  formattingOptions,
  actions,
}: {
  editor: Editor;
  formattingOptions: RichTextEditorFormattingOption[];
  actions: ToolbarActions;
}) {
  const enabledOptions = new Set(formattingOptions);
  const toolbarItems = makeToolbarItems().filter((item) =>
    enabledOptions.has(item.key),
  );

  if (!toolbarItems.length) {
    return null;
  }

  return (
    <div className={styles.richTextEditor__toolbar} aria-label="Formatting">
      {toolbarItems.map((item, index) => (
        <span key={item.key} style={{ display: 'inline-flex' }}>
          {index === 4 || index === 5 || index === 7 ? (
            <span
              className={styles.richTextEditor__toolbarSeparator}
              aria-hidden="true"
            />
          ) : null}
          <Tooltip title={item.label}>
            <button
              type="button"
              className={styles.richTextEditor__toolbarButton}
              aria-label={item.label}
              aria-pressed={item.isActive(editor)}
              disabled={!editor.isEditable}
              onClick={() => item.run(editor, actions)}
            >
              {item.icon}
            </button>
          </Tooltip>
        </span>
      ))}
    </div>
  );
}

export function RichTextEditor({
  value = '',
  placeholder = 'Write...',
  disabled = false,
  readOnly = false,
  autoFocus = false,
  footerLeft,
  footerRight,
  actionSlot,
  showSubmitButton = false,
  submitAriaLabel = 'Submit',
  isSubmitDisabled = false,
  showToolbar = true,
  formattingOptions = [...DEFAULT_FORMATTING_OPTIONS],
  extraExtensions = [],
  suggestions = [],
  onSubmit,
  onCancel,
  onChange,
}: RichTextEditorProps) {
  const onSubmitRef = useRef(onSubmit);
  const onCancelRef = useRef(onCancel);
  const onChangeRef = useRef(onChange);
  const activeSuggestionRef = useRef<ActiveSuggestion | null>(null);
  const suggestionItemsRef = useRef<RichTextEditorSuggestionItem[]>([]);
  const selectedSuggestionIndexRef = useRef(0);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [activeSuggestion, setActiveSuggestion] =
    useState<ActiveSuggestion | null>(null);
  const [suggestionItems, setSuggestionItems] = useState<
    RichTextEditorSuggestionItem[]
  >([]);
  const [suggestionSearch, setSuggestionSearch] = useState({
    key: '',
    value: '',
  });
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
    onCancelRef.current = onCancel;
    onChangeRef.current = onChange;
  }, [onCancel, onChange, onSubmit]);

  useEffect(() => {
    activeSuggestionRef.current = activeSuggestion;
  }, [activeSuggestion]);

  useEffect(() => {
    suggestionItemsRef.current = suggestionItems;
  }, [suggestionItems]);

  useEffect(() => {
    selectedSuggestionIndexRef.current = selectedSuggestionIndex;
  }, [selectedSuggestionIndex]);

  useEffect(() => {
    if (!activeSuggestion) {
      setSuggestionSearch({ key: '', value: '' });
      setIsSuggestionLoading(false);
      return;
    }

    setSuggestionSearch({
      key: `${activeSuggestion.config.trigger}:${activeSuggestion.range.from}:${activeSuggestion.range.to}`,
      value: activeSuggestion.query,
    });
  }, [activeSuggestion]);

  useEffect(() => {
    if (!activeSuggestion) {
      return;
    }

    const activeSuggestionKey = `${activeSuggestion.config.trigger}:${activeSuggestion.range.from}:${activeSuggestion.range.to}`;
    if (suggestionSearch.key !== activeSuggestionKey) {
      return;
    }

    let isDisposed = false;
    setIsSuggestionLoading(true);

    Promise.resolve(activeSuggestion.config.getItems(suggestionSearch.value))
      .then((items) => {
        if (isDisposed) {
          return;
        }

        setSuggestionItems(items);
        setSelectedSuggestionIndex(0);
      })
      .catch(() => {
        if (isDisposed) {
          return;
        }

        setSuggestionItems([]);
        setSelectedSuggestionIndex(0);
      })
      .finally(() => {
        if (!isDisposed) {
          setIsSuggestionLoading(false);
        }
      });

    return () => {
      isDisposed = true;
    };
  }, [activeSuggestion, suggestionSearch]);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      RichTextToken,
      Placeholder.configure({
        placeholder,
      }),
      ...extraExtensions,
    ],
    [extraExtensions, placeholder],
  );

  const closeSuggestions = useCallback(() => {
    setActiveSuggestion(null);
    setSuggestionItems([]);
    setIsSuggestionLoading(false);
    setSelectedSuggestionIndex(0);
  }, []);

  const insertSuggestion = useCallback(
    (editorInstance: Editor, item: RichTextEditorSuggestionItem) => {
      const currentSuggestion = activeSuggestionRef.current;

      if (!currentSuggestion) {
        return;
      }

      const label =
        currentSuggestion.config.trigger === '@'
          ? `@${item.label}`
          : `[[${item.label}]]`;

      editorInstance
        .chain()
        .focus()
        .deleteRange(currentSuggestion.range)
        .insertContent(
          `<span data-rich-text-token="${escapeHtml(
            item.canonicalValue,
          )}">${escapeHtml(label)}</span>&nbsp;`,
        )
        .run();
      closeSuggestions();
    },
    [closeSuggestions],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled && !readOnly,
    immediatelyRender: false,
    autofocus: autoFocus,
    onUpdate: ({ editor: nextEditor }) => {
      setActiveSuggestion(getSuggestionMatch(nextEditor, suggestions));
      onChangeRef.current?.(getEditorValue(nextEditor));
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
      },
      handleKeyDown: (_view, event) => {
        const currentSuggestion = activeSuggestionRef.current;
        const currentItems = suggestionItemsRef.current;

        if (currentSuggestion && currentItems.length) {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedSuggestionIndex(
              (current) => (current + 1) % currentItems.length,
            );
            return true;
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedSuggestionIndex(
              (current) =>
                (current - 1 + currentItems.length) % currentItems.length,
            );
            return true;
          }

          if (event.key === 'Enter') {
            event.preventDefault();
            const item = currentItems[selectedSuggestionIndexRef.current];
            if (item && editor) {
              insertSuggestion(editor, item);
            }
            return true;
          }
        }

        if (event.key === 'Escape') {
          if (currentSuggestion) {
            closeSuggestions();
            return true;
          }

          onCancelRef.current?.();
          return Boolean(onCancelRef.current);
        }

        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === 'l'
        ) {
          event.preventDefault();
          const currentUrl = editor?.getAttributes('link').href ?? '';
          setLinkValue(currentUrl);
          setIsLinkDialogOpen(true);
          return true;
        }

        if (
          (event.metaKey || event.ctrlKey) &&
          event.shiftKey &&
          event.key.toLowerCase() === 'x'
        ) {
          event.preventDefault();
          editor?.chain().focus().toggleStrike().run();
          return true;
        }

        if (event.key === 'Enter' && !event.shiftKey && onSubmitRef.current) {
          event.preventDefault();
          onSubmitRef.current(getEditorValue(editor!));
          return true;
        }

        return false;
      },
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled && !readOnly);
  }, [disabled, editor, readOnly]);

  useEffect(() => {
    if (!editor || editor.getHTML() === value) {
      return;
    }

    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  const submit = () => {
    if (!editor || isSubmitDisabled || disabled || readOnly) {
      return;
    }

    onSubmit?.(getEditorValue(editor));
  };

  const openLinkDialog = (currentUrl: string) => {
    setLinkValue(currentUrl);
    setIsLinkDialogOpen(true);
  };

  const closeLinkDialog = () => {
    setIsLinkDialogOpen(false);
    setLinkValue('');
  };

  const applyLink = () => {
    if (!editor) {
      return;
    }

    const trimmedLink = linkValue.trim();

    if (!trimmedLink) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      closeLinkDialog();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: trimmedLink })
      .run();
    closeLinkDialog();
  };

  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = getAnchorFromTarget(event.target);

    if (!anchor || (!event.metaKey && !event.ctrlKey)) {
      return;
    }

    const href = anchor.getAttribute('href');

    if (!href || !isSafeLinkUrl(href)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const resolvedActionSlot =
    actionSlot ??
    (showSubmitButton ? (
      <SecondaryButton
        variant="mini"
        ariaLabel={submitAriaLabel}
        disabled={isSubmitDisabled || disabled || readOnly}
        onClick={submit}
      >
        <FaArrowUp />
      </SecondaryButton>
    ) : null);

  return (
    <div
      className={clsx(styles.richTextEditor, {
        [styles.richTextEditor__disabled]: disabled || readOnly,
        [styles.richTextEditor__hasAction]: Boolean(resolvedActionSlot),
      })}
    >
      {editor && showToolbar ? (
        <Toolbar
          editor={editor}
          formattingOptions={formattingOptions}
          actions={{ openLinkDialog }}
        />
      ) : null}

      <div
        className={clsx(styles.richTextEditor__body, {
          [styles.richTextEditor__bodyDisabled]: disabled || readOnly,
        })}
        onClick={handleContentClick}
      >
        <EditorContent
          editor={editor}
          className={styles.richTextEditor__content}
        />
      </div>

      {activeSuggestion ? (
        <div className={styles.richTextEditor__suggestions}>
          <div className={styles.richTextEditor__suggestionsHeader}>
            <div className={styles.richTextEditor__suggestionsLabel}>
              {activeSuggestion.config.label}
            </div>
            <SecondaryButton
              variant="mini"
              ariaLabel="Close suggestions"
              onClick={closeSuggestions}
              backgroundOverride="theme"
            >
              <CloseIcon size={14} />
            </SecondaryButton>
          </div>
          <div className={styles.richTextEditor__suggestionsSearch}>
            <SearchBar
              placeholder={`Search ${activeSuggestion.config.label.toLowerCase()}`}
              value={suggestionSearch.value}
              onChange={(value) =>
                setSuggestionSearch((current) => ({ ...current, value }))
              }
              autoComplete="off"
              debounceMs={0}
            />
          </div>
          {isSuggestionLoading ? (
            <div className={styles.richTextEditor__suggestionsSkeleton}>
              <Skeleton variant="rounded" height={20} />
            </div>
          ) : suggestionItems.length ? (
            <div className={styles.richTextEditor__suggestionsList}>
              {suggestionItems.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={clsx(styles.richTextEditor__suggestionItem, {
                    [styles.richTextEditor__suggestionItemSelected]:
                      index === selectedSuggestionIndex,
                  })}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (editor) {
                      insertSuggestion(editor, item);
                    }
                  }}
                >
                  <strong>{item.label}</strong>
                  {item.description ? <span>{item.description}</span> : null}
                </button>
              ))}
            </div>
          ) : (
            <p className={styles.richTextEditor__suggestionsEmpty}>
              No matches found.
            </p>
          )}
        </div>
      ) : null}

      {footerLeft || footerRight || resolvedActionSlot ? (
        <footer className={styles.richTextEditor__footer}>
          <div className={styles.richTextEditor__footerLeft}>{footerLeft}</div>
          <div className={styles.richTextEditor__action}>
            {resolvedActionSlot}
          </div>
          <div className={styles.richTextEditor__footerRight}>
            {footerRight}
          </div>
        </footer>
      ) : null}

      <Modal
        open={isLinkDialogOpen}
        onClose={closeLinkDialog}
        width="min(460px, 92vw)"
      >
        <div className="card-container-glass">
          <h3 className={styles.richTextEditor__linkDialogTitle}>
            Set message link
          </h3>
          <p className={styles.richTextEditor__linkDialogDescription}>
            Paste a URL to link the selected text. Leave it empty to remove the
            link.
          </p>

          <SmartInput
            name="rich-text-editor-link-url"
            type="text"
            label="URL"
            value={linkValue}
            onChange={setLinkValue}
          />

          <div className={styles.richTextEditor__linkDialogActions}>
            <SecondaryButton ariaLabel="Cancel link" onClick={closeLinkDialog}>
              Cancel
            </SecondaryButton>
            <MainButton ariaLabel="Apply link" onClick={applyLink}>
              Apply
            </MainButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
